import { useEffect, useState } from 'react';
import { Stack, Typography, useTheme } from '@mui/material';
import { get, slice } from 'lodash';

// Components
import { StyledWhiteBox } from 'src/features/PortDetail/config';
import { renderNumberOrDefault, renderTextOrDefault } from 'src/utils/helpers/stringHelpers';
import { LIMIT_GET_LIST } from 'src/utils/constants/config';
import Table from 'src/components/Table';

// Utilities
import { IFreightRate } from 'src/utils/typings/response';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import FreightRateChart from 'src/features/PortDetail/TradeTransit/FreightRateChart';

interface IProps {
  listAllFreightRate: Array<IFreightRate>;
}

const FreightRateTable = ({ listAllFreightRate }: IProps) => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(LIMIT_GET_LIST);
  const [listFreightRate, setListFreightRate] = useState<Array<IFreightRate>>([]);
  const [freightRateSelect, setFreightRateSelect] = useState<IFreightRate | null>(null);

  useEffect(() => {
    setListFreightRate(getArrayDefault(listAllFreightRate));
    setFreightRateSelect(get(getArrayDefault(listAllFreightRate), '[0]', ''));
  }, [listAllFreightRate]);

  useEffect(() => {
    const initialList = slice(getArrayDefault(listAllFreightRate), page * pageSize, (page + 1) * pageSize);
    setListFreightRate(initialList);
  }, [listAllFreightRate, page]);

  const configColumn: ConfigCell<IFreightRate>[] = [
    {
      id: 'origin',
      disablePadding: true,
      disableSort: true,
      label: 'Origin',
      cell: (row: IFreightRate) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderTextOrDefault(row?.locode_origin)}
          </Typography>
        );
      },
    },
    {
      id: 'destination',
      disablePadding: true,
      disableSort: true,
      label: 'Destination',
      cell: (row: IFreightRate) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderTextOrDefault(row?.locode_destination)}
          </Typography>
        );
      },
    },
    {
      id: 'rate',
      disablePadding: true,
      disableSort: true,
      label: 'Rate',
      cell: (row: IFreightRate) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderNumberOrDefault(row?.freight_rate)}
          </Typography>
        );
      },
    },
    {
      id: 'currency',
      disablePadding: true,
      disableSort: true,
      label: 'Currency',
      cell: (row: IFreightRate) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderTextOrDefault(row?.currency)}
          </Typography>
        );
      },
    },
  ];

  return (
    <StyledWhiteBox display="flex" flexDirection="column" gap={2}>
      <Typography variant="h5" align="left">
        Freight rates (20ft. container)
      </Typography>
      <Stack mb={3} spacing={3} direction={'row'} alignItems="flex-start" flex={1}>
        <Stack flex={1}>
          <Table
            data={listFreightRate}
            configColumn={configColumn}
            dataKey="locode_origin"
            onChangePage={(e) => setPage(e)}
            onChangePageSize={(e) => setPageSize(e)}
            page={page}
            pageSize={pageSize}
            maxRow={10}
            totalRecords={getArrayDefault(listAllFreightRate).length}
            onRowClick={(item: IFreightRate) => setFreightRateSelect(item)}
            sxTableContainer={{
              border: `1px solid ${theme.palette.divider}`,
            }}
          />
        </Stack>
        <Stack flex={1}>
          <FreightRateChart freightRateSelect={freightRateSelect} />
        </Stack>
      </Stack>
    </StyledWhiteBox>
  );
};

export default FreightRateTable;
